'use strict';

import jQuery from 'jquery';
import VideoModel from './VideoModel.js';

var VideoCollection = (function($) {

  // Define a new VideoCollection function
  function VideoCollection(fetchAction) {

    // Define the default empty state for the model
    this._state = {
      collection: [],
      updated: 0
    };
  }

  /**
   * Fetches a video from the server based on a show id and video post name
   *
   * @param {Number} id The id of the post to reference
   * @param {String} videoType The type of video to fetch
   * @param {Number} videoOffset The offset to start the collection from
   * @param {Number} videoLimit The number of records to fetch
   * @param {boolean} [force] A boolean flag to force the fetch to ask the server
   * @returns {jQuery.Promise} A promise that resolves to a fetched collection
   */
  VideoCollection.prototype.fetch = function fetch(id, videoType, videoOffset, videoLimit, force) {

    // Create a self reference for association during callbacks
    var self = this;

    // If this model has already been fetched and a force is not required, then
    // just return a promise that will resolve with the self reference
    if (this._state.updated !== 0 && (typeof force === 'undefined' || force !== true)) {

      // Simply return a resolved promise
      return $.Deferred().resolve(self).promise();
    }

    // Create a promise to fetch the core data for the video collection
    var contentPromise = $.ajax({
      url: '/api/v1/video/',
      type: 'GET',
      dataType: 'json',
      data: {
        postId: id,
        type: videoType,
        offset: videoOffset,
        limit: videoLimit
      }
    });

    // Chain the content promise to store an updated time and ultimately provide
    // the self reference on resolution
    return contentPromise.then(function resolveShowPromise(collection) {

      // Store the resulting collection data
      self._state.collection = collection.map(function transformToVideoModel(data) {
        return new VideoModel({content: data});
      });

      // Return the collection state
      return self._state.collection;
    }).then(function updateFetchTimestamp() {

      // Update the timestamp for the last fetch
      self._state.updated = Date.now();
    }).then(function resolveFetchPromiseChain() {

      // Now that the collection has been stored, return the reference to this
      // collection as the value of the next promise resolution
      return self;
    }).promise();
  };

  /**
   * Sets the underlying list structure of the collection
   *
   * @param {Array} list An array of values to set the underlying data to
   * @returns {Array} The array the underlying data is set to
   */
  VideoCollection.prototype.set = function set(list) {

    // Set the state
    this._state.collection = list;

    return this._state.collection;
  };

  /**
   * Gets a video element at a specific index from the collection
   *
   * @param {Number} index The index of the element to get
   * @returns {VideoModel} The model at the given index
   */
  VideoCollection.prototype.at = function at(index) {

    return this._state.collection[index];
  };

  /**
   * A function for retrieving the number of elements in the collection
   *
   * @returns {Number} The number of elements in the collection
   */
  VideoCollection.prototype.length = function length() {

    return this._state.collection.length;
  };

  /**
   * Performs a traditional map over the underlying list and returns a new
   * collection of the transformed list
   *
   * @param {Function} fn The function to map with
   * @param {Object} thisArg The argument to use as the this value
   * @returns {VideoCollection} A new collection of the mapped elements
   */
  VideoCollection.prototype.map = function map(fn, thisArg) {

    // Create a new collection
    var c = new VideoCollection();

    // Set the underlying list of the collection to the map of the original
    c.set(this._state.collection.map(fn, thisArg));

    // Return the new collection
    return c;
  };

  /**
   * Executes a function on each of the elements of the underlying list
   *
   * @param {Function} fn The function to execute on each element
   * @param {Object} thisArg The argument to use as the this value
   */
  VideoCollection.prototype.forEach = function forEach(fn, thisArg) {

    // Simply loop through the collection calling the function on each
    this._state.collection.forEach(fn, thisArg);
  };

  /**
   * Slices the collection and returns a new collection. Uses the underlying
   * array's slice method
   *
   * @param {Number} begin Zero based index to start extraction
   * @param {Number} end Zero-based index at which to end extraction, non-inclusive.
   * @returns {VideoCollection}
   */
  VideoCollection.prototype.slice = function slice(begin, end) {

    // Create a new collection
    var c = new VideoCollection();

    // Set the underlying list of the collection to the map of the original
    c.set(this._state.collection.slice(begin, end));

    // Return the new collection
    return c;
  };

  /**
   * Returns the collection in array format
   *
   * @returns {Array} The collection as an array
   */
  VideoCollection.prototype.toArray = function toArray() {

    return this._state.collection;
  };

  return VideoCollection;
})(jQuery);

export default VideoCollection